/**
 * ScheduledReportsContent - Embedded content for Scheduled Reports
 * Used within the ReportsPage as a tab
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Calendar, RefreshCw, Plus, Play, Pause, Trash2,
  Clock, FileText, Mail, Settings, BarChart3, CheckCircle,
  XCircle, Loader2, Edit, Eye, Send, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly'
};

const REPORT_TYPE_LABELS = {
  tmf_completeness: 'TMF Completeness',
  compliance_summary: 'Compliance Summary',
  document_activity: 'Document Activity',
  audit_trail: 'Audit Trail',
  study_health: 'Study Health'
};

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ScheduledReportsContent() {
  const { getAuthHeaders, selectedStudy: contextStudy } = useAuth();

  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [studies, setStudies] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('schedules');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Form state - Use context study as default
  const [formData, setFormData] = useState({
    name: '',
    report_type: 'tmf_completeness',
    frequency: 'weekly',
    delivery_hour: 8,
    delivery_day: 1,
    recipients: [],
    parameters: { study_id: contextStudy?.study_id || undefined },
    include_csv: true,
    enabled: true
  });

  // Set selected study from context when creating new schedule
  useEffect(() => {
    if (contextStudy?.study_id) {
      setFormData(prev => ({ 
        ...prev, 
        parameters: { ...prev.parameters, study_id: contextStudy.study_id }
      }));
    }
  }, [contextStudy]);
  
  const [recipientInput, setRecipientInput] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();
      
      const [schedulesRes, statsRes, studiesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduled-reports`, { headers }),
        axios.get(`${API_URL}/api/scheduled-reports/stats/overview`, { headers }),
        axios.get(`${API_URL}/api/studies`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers }).catch(() => ({ data: { users: [] } }))
      ]);
      
      let schedulesData = schedulesRes.data?.schedules || [];
      
      // Filter by context study if set
      if (contextStudy?.study_id) {
        schedulesData = schedulesData.filter(s => 
          s.parameters?.study_id === contextStudy.study_id || !s.parameters?.study_id
        );
      }
      
      setSchedules(schedulesData);
      setStats(statsRes.data);
      setStudies(studiesRes.data || []);
      setUsers(usersRes.data?.users || usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, contextStudy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      report_type: 'tmf_completeness',
      frequency: 'weekly',
      delivery_hour: 8,
      delivery_day: 1,
      recipients: [],
      parameters: { study_id: contextStudy?.study_id || undefined },
      include_csv: true,
      enabled: true
    });
    setRecipientInput('');
  };

  const createSchedule = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a schedule name');
      return;
    }
    if (formData.recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }

    setActionLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/scheduled-reports`, formData, { headers });
      toast.success('Schedule created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const updateSchedule = async () => {
    if (!selectedSchedule) return;
    
    setActionLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/scheduled-reports/${selectedSchedule.id}`, formData, { headers });
      toast.success('Schedule updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteSchedule = async () => {
    if (!selectedSchedule) return;
    
    setActionLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.delete(`${API_URL}/api/scheduled-reports/${selectedSchedule.id}`, { headers });
      toast.success('Schedule deleted successfully');
      setShowDeleteDialog(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSchedule = async (schedule) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/scheduled-reports/${schedule.id}`, {
        ...schedule,
        enabled: !schedule.enabled
      }, { headers });
      toast.success(`Schedule ${schedule.enabled ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to toggle schedule');
    }
  };

  const runNow = async (schedule) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/scheduled-reports/${schedule.id}/run-now`, {}, { headers });
      toast.success('Report generation triggered');
      fetchData();
    } catch (error) {
      console.error('Run error:', error);
      toast.error(error.response?.data?.detail || 'Failed to run schedule');
    }
  };

  const previewReport = async () => {
    setPreviewLoading(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/reports/preview`, {
        report_type: formData.report_type,
        parameters: formData.parameters
      }, { headers });
      
      setPreviewData(response.data);
      setShowPreviewDialog(true);
      toast.success('Preview generated');
      
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      name: schedule.name || '',
      report_type: schedule.report_type || 'tmf_completeness',
      frequency: schedule.frequency || 'weekly',
      delivery_hour: schedule.delivery_hour || 8,
      delivery_day: schedule.delivery_day || 1,
      recipients: schedule.recipients || [],
      parameters: schedule.parameters || {},
      include_csv: schedule.include_csv !== false,
      enabled: schedule.enabled !== false
    });
    setShowEditDialog(true);
  };

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (email && !formData.recipients.includes(email)) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setFormData(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
        setRecipientInput('');
      } else {
        toast.error('Invalid email address');
      }
    }
  };

  const removeRecipient = (email) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const getStatusBadge = (schedule) => {
    if (!schedule.enabled) {
      return <Badge className="bg-slate-500">Disabled</Badge>;
    }
    if (schedule.last_status === 'failed') {
      return <Badge className="bg-red-500">Failed</Badge>;
    }
    if (schedule.last_status === 'success') {
      return <Badge className="bg-green-500">Active</Badge>;
    }
    return <Badge className="bg-blue-500">Pending</Badge>;
  };

  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'Not scheduled';
    try {
      const date = parseISO(nextRun);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  // Form Dialog Content (reused for create/edit)
  const renderFormDialog = (isEdit = false) => (
    <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit Schedule' : 'Create New Schedule'}</DialogTitle>
        <DialogDescription>
          {isEdit ? 'Modify the scheduled report settings' : 'Set up automated report generation and delivery'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {/* Schedule Name */}
        <div className="space-y-2">
          <Label>Schedule Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Weekly TMF Report"
            data-testid="schedule-name-input"
          />
        </div>

        {/* Report Type */}
        <div className="space-y-2">
          <Label>Report Type</Label>
          <Select 
            value={formData.report_type} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, report_type: v }))}
          >
            <SelectTrigger data-testid="report-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(REPORT_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Frequency */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select 
              value={formData.frequency} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v }))}
            >
              <SelectTrigger data-testid="frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Delivery Time</Label>
            <Select 
              value={String(formData.delivery_hour)} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, delivery_hour: parseInt(v) }))}
            >
              <SelectTrigger data-testid="hour-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {i.toString().padStart(2, '0')}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Day Selection for weekly/biweekly */}
        {(formData.frequency === 'weekly' || formData.frequency === 'biweekly') && (
          <div className="space-y-2">
            <Label>Day of Week</Label>
            <Select 
              value={String(formData.delivery_day)} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, delivery_day: parseInt(v) }))}
            >
              <SelectTrigger data-testid="day-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_LABELS.map((day, i) => (
                  <SelectItem key={i} value={String(i)}>{day}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Study Selection - Only show if no context study */}
        {!contextStudy?.study_id && (
          <div className="space-y-2">
            <Label>Study (Optional)</Label>
            <Select 
              value={formData.parameters?.study_id || 'all'} 
              onValueChange={(v) => setFormData(prev => ({ 
                ...prev, 
                parameters: { ...prev.parameters, study_id: v === 'all' ? undefined : v }
              }))}
            >
              <SelectTrigger data-testid="study-select">
                <SelectValue placeholder="All Studies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Studies</SelectItem>
                {studies.map(study => (
                  <SelectItem key={study.id} value={study.id}>{study.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Recipients */}
        <div className="space-y-2">
          <Label>Recipients</Label>
          <div className="flex gap-2">
            <Input
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              placeholder="Enter email address"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
              data-testid="recipient-input"
            />
            <Button type="button" onClick={addRecipient} variant="outline" data-testid="add-recipient-btn">
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.recipients.map((email) => (
              <Badge key={email} variant="secondary" className="flex items-center gap-1">
                {email}
                <button onClick={() => removeRecipient(email)} className="ml-1 hover:text-red-500">
                  <XCircle className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.include_csv}
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, include_csv: v }))}
              data-testid="include-csv-switch"
            />
            <Label>Include CSV attachment</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.enabled}
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, enabled: v }))}
              data-testid="enabled-switch"
            />
            <Label>Enabled</Label>
          </div>
        </div>
      </div>

      <DialogFooter className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={previewReport}
          disabled={previewLoading}
          data-testid="preview-btn"
        >
          {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          Preview
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => isEdit ? setShowEditDialog(false) : setShowCreateDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={isEdit ? updateSchedule : createSchedule}
            disabled={actionLoading}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-btn"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-slate-800">Automated Report Schedules</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            data-testid="refresh-schedules-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-schedule-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Schedule
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-slate-50/90 border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Schedules</p>
                  <p className="text-2xl font-bold text-slate-800">{stats.total_schedules || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50/90 border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active_schedules || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50/90 border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Reports Sent (30d)</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.reports_sent_30d || 0}</p>
                </div>
                <Send className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50/90 border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed_schedules || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <Card className="bg-slate-50/90 border-slate-200">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No scheduled reports yet</p>
            <Button 
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Schedule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {schedules.map((schedule) => (
            <Card key={schedule.id} className="bg-slate-50/90 border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${schedule.enabled ? 'bg-indigo-100' : 'bg-slate-200'}`}>
                      <FileText className={`w-5 h-5 ${schedule.enabled ? 'text-indigo-600' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-800">{schedule.name}</h4>
                        {getStatusBadge(schedule)}
                      </div>
                      <p className="text-sm text-slate-500">
                        {REPORT_TYPE_LABELS[schedule.report_type]} • {FREQUENCY_LABELS[schedule.frequency]}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Next Run</p>
                      <p className="text-sm font-medium text-slate-700">
                        {formatNextRun(schedule.next_run)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Recipients</p>
                      <p className="text-sm font-medium text-slate-700">
                        {schedule.recipients?.length || 0} email(s)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleSchedule(schedule)}
                        title={schedule.enabled ? 'Disable' : 'Enable'}
                      >
                        {schedule.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => runNow(schedule)}
                        disabled={!schedule.enabled}
                        title="Run Now"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(schedule)}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedSchedule(schedule); setShowDeleteDialog(true); }}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        {renderFormDialog(false)}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        {renderFormDialog(true)}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Delete Schedule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedSchedule?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteSchedule}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Preview</DialogTitle>
            <DialogDescription>
              Sample of what the report will contain
            </DialogDescription>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2">Report: {REPORT_TYPE_LABELS[formData.report_type]}</h4>
                <p className="text-sm text-slate-600">
                  Generated at: {previewData.generated_at ? format(parseISO(previewData.generated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                </p>
              </div>
              {previewData.summary && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h5 className="font-medium mb-2">Summary</h5>
                  <pre className="text-sm whitespace-pre-wrap">{JSON.stringify(previewData.summary, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
